import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Printer, Home, MapPin, Calendar, Clock, Users, Car, CreditCard, Mail, Phone, User, ArrowRight } from "lucide-react";
import { format } from "date-fns";
import logoUrl from "@/assets/logo-aot.png";

const BookingConfirmation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as {
    booking: any;
    vehicle: { name: string; image_url: string | null };
  } | null;

  if (!state?.booking) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <p className="text-muted-foreground font-sans">No booking information found.</p>
          <Button onClick={() => navigate("/")}>Go Home</Button>
        </div>
      </div>
    );
  }

  const { booking, vehicle } = state;

  const paymentMethodLabels: Record<string, string> = {
    cash: "Cash",
    paypal: "PayPal",
    stripe: "Credit / Debit Card",
  };

  const statusColors: Record<string, string> = {
    pending: "bg-yellow-500/15 text-yellow-700 border-yellow-200",
    confirmed: "bg-blue-500/15 text-blue-700 border-blue-200",
    completed: "bg-green-500/15 text-green-700 border-green-200",
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header - hidden on print */}
      <header className="bg-hero-bg py-4 border-b border-hero-foreground/10 print:hidden">
        <div className="container mx-auto px-4 flex items-center justify-between">
          <a href="/" className="inline-block">
            <img src={logoUrl} alt="Always On Time Transfers Tours" className="h-12 w-auto rounded-lg" />
          </a>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8 max-w-3xl">
        {/* Success Banner */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-500/15 mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-foreground">
            Booking <span className="text-primary">Confirmed!</span>
          </h1>
          <p className="text-muted-foreground font-sans mt-2">Your transfer has been successfully booked.</p>
        </div>

        {/* Voucher Card */}
        <div className="bg-card border border-border rounded-2xl overflow-hidden shadow-lg print:shadow-none print:border-2">
          {/* Booking Code Header */}
          <div className="bg-hero-bg px-6 py-5 text-center">
            <p className="text-hero-foreground/60 font-sans text-xs uppercase tracking-widest mb-1">Booking Code</p>
            <p className="text-3xl font-display font-bold text-primary tracking-wider">
              {booking.booking_code || "N/A"}
            </p>
          </div>

          <div className="p-6 space-y-6">
            {/* Route */}
            <div className="space-y-3">
              <h3 className="font-sans text-xs uppercase tracking-widest text-muted-foreground font-semibold">Route Details</h3>
              <div className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                <div className="flex-1">
                  <p className="font-sans text-sm font-medium text-foreground">{booking.pickup_location}</p>
                  <div className="flex items-center gap-2 my-1">
                    <ArrowRight className="w-3 h-3 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground font-sans">
                      {booking.route_type === "round-trip" ? "Round Trip" : "One Way"}
                      {booking.distance_km && ` • ${booking.distance_km} km`}
                      {booking.estimated_time_min && ` • ~${booking.estimated_time_min} min`}
                    </span>
                  </div>
                  <p className="font-sans text-sm font-medium text-foreground">{booking.dropoff_location}</p>
                </div>
              </div>
            </div>

            {/* Date & Time */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center gap-3">
                <Calendar className="w-4 h-4 text-primary shrink-0" />
                <div>
                  <p className="text-xs text-muted-foreground font-sans">Pickup Date</p>
                  <p className="font-sans text-sm font-medium text-foreground">
                    {booking.pickup_date ? format(new Date(booking.pickup_date), "PPP 'at' p") : "N/A"}
                  </p>
                </div>
              </div>
              {booking.return_date && (
                <div className="flex items-center gap-3">
                  <Calendar className="w-4 h-4 text-primary shrink-0" />
                  <div>
                    <p className="text-xs text-muted-foreground font-sans">Return Date</p>
                    <p className="font-sans text-sm font-medium text-foreground">
                      {(() => { try { return format(new Date(booking.return_date), "PPP 'at' p"); } catch { return "N/A"; } })()}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Vehicle & Passengers */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center gap-3">
                <Car className="w-4 h-4 text-primary shrink-0" />
                <div>
                  <p className="text-xs text-muted-foreground font-sans">Vehicle</p>
                  <p className="font-sans text-sm font-medium text-foreground">{vehicle.name}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Users className="w-4 h-4 text-primary shrink-0" />
                <div>
                  <p className="text-xs text-muted-foreground font-sans">Passengers</p>
                  <p className="font-sans text-sm font-medium text-foreground">{booking.passengers}</p>
                </div>
              </div>
            </div>

            {/* Flight info */}
            {booking.flight_number && (
              <div className="flex items-center gap-3">
                <Clock className="w-4 h-4 text-primary shrink-0" />
                <div>
                  <p className="text-xs text-muted-foreground font-sans">Flight</p>
                  <p className="font-sans text-sm font-medium text-foreground">{booking.flight_number}</p>
                </div>
              </div>
            )}

            <hr className="border-border" />

            {/* Customer Info */}
            <div className="space-y-3">
              <h3 className="font-sans text-xs uppercase tracking-widest text-muted-foreground font-semibold">Customer Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-primary shrink-0" />
                  <span className="font-sans text-sm text-foreground">{booking.customer_name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-primary shrink-0" />
                  <span className="font-sans text-sm text-foreground">{booking.customer_email}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-primary shrink-0" />
                  <span className="font-sans text-sm text-foreground">{booking.customer_phone}</span>
                </div>
              </div>
            </div>

            <hr className="border-border" />

            {/* Payment & Total */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-primary" />
                  <span className="font-sans text-sm text-muted-foreground">Payment:</span>
                  <span className="font-sans text-sm font-medium text-foreground">
                    {paymentMethodLabels[booking.payment_method] || booking.payment_method}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className={statusColors[booking.payment_status] || statusColors.pending}>
                    {booking.payment_status === "paid" ? "Paid" : booking.payment_status === "pending" ? "Pending" : "Unpaid"}
                  </Badge>
                  <Badge variant="outline" className={statusColors[booking.status] || statusColors.pending}>
                    {booking.status === "pending" ? "Pending" : booking.status}
                  </Badge>
                </div>
              </div>
              <div className="text-right">
                <p className="text-xs text-muted-foreground font-sans">Total Price</p>
                <p className="text-3xl font-display font-bold text-primary">${booking.total_price}</p>
              </div>
            </div>

            {/* Special Requests */}
            {booking.special_requests && (
              <div className="bg-muted/50 rounded-xl p-4">
                <p className="text-xs text-muted-foreground font-sans uppercase tracking-widest mb-1">Special Requests</p>
                <p className="font-sans text-sm text-foreground">{booking.special_requests}</p>
              </div>
            )}
          </div>
        </div>

        {/* QR Code & Voucher Link */}
        {booking.booking_code && (
          <div className="bg-card border border-border rounded-2xl p-6 mt-6 text-center space-y-4">
            <h3 className="font-sans text-xs uppercase tracking-widest text-muted-foreground font-semibold">Verification QR Code</h3>
            <img
              src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(`${window.location.origin}/voucher/${booking.booking_code}`)}`}
              alt="Voucher QR Code"
              className="mx-auto rounded-lg"
              width={180}
              height={180}
            />
            <a
              href={`/voucher/${booking.booking_code}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-primary hover:underline font-sans font-medium"
            >
              View Public Voucher →
            </a>
          </div>
        )}

        {/* Actions - hidden on print */}
        <div className="flex flex-col sm:flex-row gap-3 mt-6 print:hidden">
          <Button onClick={() => window.print()} variant="outline" className="flex-1 font-sans">
            <Printer className="w-4 h-4 mr-2" /> Print Voucher
          </Button>
          <Button onClick={() => navigate("/")} className="flex-1 font-sans font-bold">
            <Home className="w-4 h-4 mr-2" /> Back to Home
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BookingConfirmation;
